import {
    addToHistoryStack,
    setDeletedId,
    setIsDeleting,
    setIsAutoSaving,
    updateHistoryItemDataId,
    updateHistoryItemData,
    updateHistoryItemTitle,
    setIsBusy,
    patchCabinetAddHistoryTitleByCabinetId,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {invalidateProductAndCost} from 'components/customer/Product/store/productApi';
import {store} from 'store/customer';
import * as THREE from 'three';
import {deleteCabinetMesh} from 'components/customer/RoomPlanner/helpers/deleteCabinetMesh';
import type {HistoryActionContext} from 'components/customer/RoomPlanner/helpers/history/historyActionContext';
import type {
    HistoryStack,
    UndoActionContext,
} from 'components/customer/RoomPlanner/types';
import type {AppDispatch} from 'store/customer/storeSetup';

export interface DispatchCabinetAddToHistoryPayload {
    title: string;
    data: {
        id: number;
        redoAction: (ctx?: UndoActionContext) => void | Promise<void>;
        shakerDoorInfo?: {isShakerDoor: boolean};
        material?: unknown;
        door?: unknown;
    };
}

export const dispatchCabinetAddToHistory = (
    dispatch: AppDispatch,
    payload: DispatchCabinetAddToHistoryPayload
): void => {
    dispatch(
        addToHistoryStack({
            action: 'CABINET_ADD',
            isActive: true,
            timestamp: Date.now(),
            title: payload.title,
            data: payload.data,
        })
    );

    _pendingCabinetIds.add(payload.data.id);
    _scheduleFallbackRefetch();
};

const _pendingCabinetIds = new Set<number>();
let _fallbackRefetchTimer: ReturnType<typeof setTimeout> | null = null;
const FALLBACK_REFETCH_DELAY_MS = 5_000;

const _scheduleFallbackRefetch = (): void => {
    if (_fallbackRefetchTimer !== null) {
        clearTimeout(_fallbackRefetchTimer);
    }

    _fallbackRefetchTimer = setTimeout(() => {
        _fallbackRefetchTimer = null;

        if (_pendingCabinetIds.size === 0) return;

        const pendingIds = Array.from(_pendingCabinetIds);
        const historyStack = store.getState().roomPlanner.historyStack;
        const hasUnresolved = pendingIds.some((cabinetId) =>
            historyStack.some(
                (item) =>
                    item.action === 'CABINET_ADD' &&
                    Number(item.data?.id) === cabinetId &&
                    item.title?.includes('...')
            )
        );

        _pendingCabinetIds.clear();

        if (hasUnresolved) {
            store.dispatch(invalidateProductAndCost());
        }
    }, FALLBACK_REFETCH_DELAY_MS);
};

// Pending updates keyed by cabinetId — only the latest roomCabNumber per
// cabinet is kept, so rapid duplicate calls from the same render cycle are
// coalesced into a single dispatch before the microtask checkpoint.
const _titleUpdateQueue = new Map<number, number>();
let _titleUpdateFlushScheduled = false;

const _deduplicateHistoryNumbers = (
    cabinetId: number,
    roomCabNumber: number,
    roomIndex: number | undefined
): void => {
    if (roomIndex == null) return;
    const displayRoomIndex = String(roomIndex + 1);
    const historyStack = store.getState().roomPlanner.historyStack;
    let nextConflict = roomCabNumber;

    for (let i = 0; i < historyStack.length; i++) {
        const item = historyStack[Number(i)];
        if (
            item.action !== 'CABINET_ADD' ||
            Number(item.data?.id) === cabinetId
        ) {
            continue;
        }
        const m = (item.title ?? '').match(/\[(\d+)-(\d+)\]/);
        if (m?.[1] !== displayRoomIndex) continue;
        if (parseInt(m[2], 10) === nextConflict) {
            const bumped = (item.title ?? '').replace(
                /\[(\d+)-(?:\d+|\.\.\.)\]/,
                (_match, ri: string) => `[${ri}-${nextConflict + 1}]`
            );
            store.dispatch(updateHistoryItemTitle({index: i, title: bumped}));
            nextConflict++;
        }
    }
};

const _resolveDeadlockHistoryEntry = (
    cabinetId: number,
    roomCabNumber: number,
    roomIndex: number | undefined
): void => {
    if (roomIndex == null) return;
    const displayRoomIndex = String(roomIndex + 1);
    const historyStack = store.getState().roomPlanner.historyStack;

    let nanIdx = -1;
    for (let i = historyStack.length - 1; i >= 0; i--) {
        const h = historyStack[Number(i)];
        if (
            h.action !== 'CABINET_ADD' ||
            h.data?.id == null ||
            !Number.isNaN(Number(h.data.id)) ||
            !h.title?.includes('...')
        ) {
            continue;
        }
        const m = (h.title ?? '').match(/\[(\d+)-\.\.\.\]/);
        if (m?.[1] === displayRoomIndex) {
            nanIdx = i;
            break;
        }
    }

    if (nanIdx === -1) return;

    let maxBefore = 0;
    for (let j = 0; j < nanIdx; j++) {
        const prev = historyStack[Number(j)];
        if (prev.action !== 'CABINET_ADD') continue;
        const m = (prev.title ?? '').match(/\[(\d+)-(\d+)\]/);
        if (m?.[1] === displayRoomIndex) {
            maxBefore = Math.max(maxBefore, parseInt(m[2], 10));
        }
    }
    const inferredNum = maxBefore + 1;

    let nextConflict = inferredNum;
    for (let k = nanIdx + 1; k < historyStack.length; k++) {
        const later = historyStack[Number(k)];
        if (later.action !== 'CABINET_ADD') continue;
        const m = (later.title ?? '').match(/\[(\d+)-(\d+)\]/);
        if (m?.[1] !== displayRoomIndex) continue;
        const laterNum = parseInt(m[2], 10);
        if (laterNum === nextConflict) {
            const bumped = (later.title ?? '').replace(
                /\[(\d+)-(?:\d+|\.\.\.)\]/,
                (_match, ri: string) => `[${ri}-${laterNum + 1}]`
            );
            store.dispatch(updateHistoryItemTitle({index: k, title: bumped}));
            nextConflict = laterNum + 1;
        } else if (laterNum > nextConflict) {
            break;
        }
    }

    const nanItem = historyStack[Number(nanIdx)];
    const updated = (nanItem.title ?? '').replace(
        /\[(\d+)-(?:\d+|\.\.\.)\]/,
        (_match, ri: string) => `[${ri}-${inferredNum}]`
    );
    if (updated !== nanItem.title) {
        store.dispatch(updateHistoryItemTitle({index: nanIdx, title: updated}));
        store.dispatch(
            updateHistoryItemData({index: nanIdx, data: {id: cabinetId}})
        );
    }
    void roomCabNumber;
};

const _flushTitleUpdateQueue = (): void => {
    _titleUpdateFlushScheduled = false;
    const roomIndex = store.getState().roomPlanner.roomIndex ?? undefined;

    // Dispatch one action per cabinetId. The reducer performs the
    // historyStack search itself on the live Redux state, so there is no
    // stale snapshot problem: it will always find the correct entry even
    // when the stack has been shifted/trimmed by concurrent adds.
    _titleUpdateQueue.forEach((roomCabNumber, cabinetId) => {
        const historyStack = store.getState().roomPlanner.historyStack;
        const hasNormalEntry = historyStack.some(
            (item) =>
                item.action === 'CABINET_ADD' &&
                Number(item.data?.id) === cabinetId
        );

        if (hasNormalEntry) {
            // Normal cabinet: patch its title then fix any premature duplicates.
            store.dispatch(
                patchCabinetAddHistoryTitleByCabinetId({
                    cabinetId,
                    roomCabNumber,
                })
            );
            _deduplicateHistoryNumbers(cabinetId, roomCabNumber, roomIndex);
        } else {
            // Deadlock cabinet (data.id = NaN in history): resolve by inferred
            // history-order position rather than the DB-assigned number.
            _resolveDeadlockHistoryEntry(cabinetId, roomCabNumber, roomIndex);
        }
    });

    _titleUpdateQueue.clear();
};

export const updateCabinetAddHistoryTitleRoomCabNumber = (
    cabinetId: number,
    roomCabNumber: number
): void => {
    if (!cabinetId || !roomCabNumber) return;

    _pendingCabinetIds.delete(cabinetId);
    if (_pendingCabinetIds.size === 0 && _fallbackRefetchTimer !== null) {
        clearTimeout(_fallbackRefetchTimer);
        _fallbackRefetchTimer = null;
    }

    // Overwrite with the latest value — deduplicates rapid successive calls
    // for the same cabinet before the microtask flush runs.
    _titleUpdateQueue.set(cabinetId, roomCabNumber);

    if (!_titleUpdateFlushScheduled) {
        _titleUpdateFlushScheduled = true;
        queueMicrotask(_flushTitleUpdateQueue);
    }
};

export const undoCabinetAdd = async (
    historyItem: HistoryStack,
    targetIndex: number,
    ctx: HistoryActionContext
): Promise<void> => {
    const cabinetToDelete = ctx.cabinetGroupListRef.current?.find((cabinet) => {
        const cabinetId = String(
            cabinet.userData?.values?.id ||
                cabinet.userData?.values?.job_cabinet_id ||
                ''
        );
        const cabinetIdNumber = Number(cabinetId.split('-')[0] || cabinetId);
        return cabinetIdNumber == historyItem.data?.id;
    });

    if (!cabinetToDelete) {
        ctx.setPreviousHistoryIndex(targetIndex);
        return;
    }

    const rawUserData = cabinetToDelete?.userData;
    if (!rawUserData?.values) {
        ctx.setPreviousHistoryIndex(targetIndex);
        return;
    }

    // Capture the cabinet's last position before deleting so redo can restore it.
    const lastPosition =
        cabinetToDelete.position instanceof THREE.Vector3
            ? cabinetToDelete.position.clone()
            : new THREE.Vector3().copy(
                  cabinetToDelete.position as THREE.Vector3
              );

    if (lastPosition && ctx.dispatch) {
        ctx.dispatch(
            updateHistoryItemData({
                index: targetIndex,
                data: {
                    previousPosition: lastPosition,
                },
            })
        );
    }

    const cabinetId = Number(rawUserData.values?.id);
    const product = Number(rawUserData.values.type);

    if (cabinetId) {
        ctx.dispatch(setIsDeleting(true));
        ctx.dispatch(setIsAutoSaving(true));
        ctx.deselectEditingCabinet(cabinetId, product);
        ctx.deleteProduct({cabinetId})
            .then(() => {
                ctx.dispatch(setDeletedId(cabinetId));
                deleteCabinetMesh(
                    cabinetToDelete,
                    false,
                    ctx.cabinetGroupListRef
                );
                ctx.updateTotalProductCountManual(false, false, 1);
                ctx.invalidateCaches();
            })
            .catch(() => {
                ctx.invalidateCaches();
            })
            .finally(() => {
                ctx.dispatch(setIsDeleting(false));
                ctx.dispatch(setIsAutoSaving(false));
            });
    }

    ctx.setPreviousHistoryIndex(targetIndex);

    return Promise.resolve();
};

export const redoCabinetAdd = async (
    historyItem: HistoryStack,
    targetIndex: number,
    ctx: HistoryActionContext
): Promise<void> => {
    const existingCabinet = ctx.cabinetGroupListRef.current?.find(
        (cabinet) =>
            Number(cabinet.userData?.values?.job_cabinet_id) ==
            historyItem.data?.id
    );
    const anyTempMeshExists = ctx.cabinetGroupListRef.current?.find(
        (cabinet) => cabinet.userData.isTempMesh
    );

    if (Boolean(anyTempMeshExists)) return;

    if (!existingCabinet && historyItem.data?.redoAction) {
        const oldId = historyItem.data.id;
        const numOldId = oldId !== undefined ? Number(oldId) : Number.NaN;
        const redoAction = historyItem.data.redoAction;

        ctx.dispatch(setIsBusy(true));

        try {
            await redoAction({
                setRestoredCabinetId: (id: number) => {
                    if (Number.isNaN(numOldId)) return;
                    ctx.historyStack.forEach((item, index) => {
                        if (
                            Number(item.data?.id) === numOldId &&
                            (item.action === 'CABINET_ADD' ||
                                item.action === 'CABINET_DELETE' ||
                                item.action === 'POSITION_CHANGE' ||
                                item.action === 'CABINET_UPDATE')
                        ) {
                            ctx.dispatch(
                                updateHistoryItemDataId({
                                    index,
                                    newId: id,
                                })
                            );
                        }
                    });

                    setTimeout(() => {
                        ctx.dispatch(setIsBusy(false));
                    }, 200);
                },
            });
        } catch {
            // ignore errors from redoAction
        }
    }

    await ctx.setNextHistoryIndex(targetIndex, ctx.targetIndex);
};
